import { ReactNode, createContext, useCallback, useMemo } from "react";
import useSessionStorage from "hooks/useSessionStorage";
import { CustomBreadcrumbsProps } from "components/custom-breadcrumbs";

export type BreadcrumbContextProps = {
  breadcrumbs: CustomBreadcrumbsProps;
  onSetBreadcrumbs: (props: CustomBreadcrumbsProps) => void;
};

const initialState: BreadcrumbContextProps = {
  breadcrumbs: {
    heading: undefined,
    moreLink: undefined,
    activeLast: undefined,
    showEditButton: false,
    showSaveButton: false,
    showCancelButton: false,
    onEditClicked: undefined,
    onSaveClicked: undefined,
    onCancelClicked: undefined,
    onSaveEnabled: undefined,
    links: [],
  },
  onSetBreadcrumbs: (props: CustomBreadcrumbsProps) => {},
};

const BreadcrumbContext = createContext(initialState);

type BreadcrumbProviderProps = {
  children: ReactNode;
};

function BreadcrumbProvider({ children }: BreadcrumbProviderProps) {
  const [breadcrumbs, setBreadcrumbs] = useSessionStorage<CustomBreadcrumbsProps>("breadcrumbs", {
    heading: undefined,
    moreLink: undefined,
    activeLast: undefined,
    showEditButton: false,
    showSaveButton: false,
    showCancelButton: false,
    onEditClicked: undefined,
    onSaveClicked: undefined,
    onCancelClicked: undefined,
    onSaveEnabled: undefined,
    links: [],
  });

  const onSetBreadcrumbs = useCallback(
    (props: CustomBreadcrumbsProps) => {
      setBreadcrumbs({
        ...breadcrumbs,
        heading: props.heading,
        moreLink: props.moreLink,
        activeLast: props.activeLast,
        showEditButton: props.showEditButton,
        showSaveButton: props.showSaveButton,
        showCancelButton: props.showCancelButton,
        onEditClicked: props.onEditClicked,
        onSaveClicked: props.onSaveClicked,
        onCancelClicked: props.onCancelClicked,
        onSaveEnabled: props.onSaveEnabled,
        links: props.links,
      });
    },
    [setBreadcrumbs, breadcrumbs]
  );

  const memoizedValue = useMemo(
    () => ({
      breadcrumbs,
      onSetBreadcrumbs,
    }),
    [breadcrumbs, onSetBreadcrumbs]
  );
  return <BreadcrumbContext.Provider value={memoizedValue}>{children}</BreadcrumbContext.Provider>;
}

export { BreadcrumbProvider, BreadcrumbContext };
